import Image, { ImageProps } from 'next/image'
import { useMemo } from 'react'

import getImageUrl from '../utils/getImageUrl'

type OptimizedImageProps = ImageProps & {
  width?: number
  height?: number
  fallbackSize?: string
  mediaId?: string | null
  modified?: string | null
}

const MediaAPIImageSizes = ['100x100', '200x200', '400x400', '600x600']

function getClosestSize(width?: number, height?: number) {
  if (width || height) {
    const targetSize = Math.max(width || 0, height || 0)

    // Find the closest available size
    for (const size of MediaAPIImageSizes) {
      const sizeValue = parseInt(size.split('x')[0], 10)
      if (sizeValue >= targetSize) {
        return size
      }
    }
  }

  // If all available sizes are smaller than target, return the largest one
  return MediaAPIImageSizes[MediaAPIImageSizes.length - 1]
}

export default function OptimizedImage({
  mediaId,
  src,
  width,
  height,
  quality,
  modified,
  ...rest
}: OptimizedImageProps) {
  const processedSrc = useMemo(() => {
    // Case 1: MediaID - get the image URL with the closest available size
    if (mediaId) {
      const imageSize = getClosestSize(width, height)

      return getImageUrl(mediaId, { imageSize, ...(modified && { modified }) })
    }

    // Case 2: StaticImport - pass src as it is
    if (typeof src === 'object') {
      return src
    }

    // Case 3: Local path - pass src as it is
    if (typeof src === 'string' && src.startsWith('/')) {
      return src
    }

    return src
  }, [src, width, height, modified, mediaId])

  return (
    <Image
      src={processedSrc}
      width={width}
      height={height}
      quality={quality}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  )
}
